import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { API_BASE } from "@/lib/apiBase";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import PhotoGallery from "@/pages/PhotoGallery";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Orders from "@/pages/Orders";
import AdminClasses from "@/pages/admin/AdminClasses";
import AdminStudents from "@/pages/admin/AdminStudents";
import AdminPhotos from "@/pages/admin/AdminPhotos";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPrintFormats from "@/pages/admin/AdminPrintFormats";
import AdminBundles from "@/pages/admin/AdminBundles";
import AdminSiteSettings from "@/pages/admin/AdminSiteSettings";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminAbandonedCarts from "@/pages/admin/AdminAbandonedCarts";
import ImpressumPage from "@/pages/ImpressumPage";
import PrivacyPage from "@/pages/PrivacyPage";
import LinkStudent from "@/pages/LinkStudent";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageContext";
import { ThemeProvider } from "@/context/ThemeContext";

// Layout
import { Layout } from "@/components/Layout";

// Analytics & Consent
import { CookieConsent } from "@/components/CookieConsent";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";

// Stable role gates — module-level so allowedRoles passes the same array
// reference on every render (avoids ProtectedRoute receiving new props).
const ADMIN_ROLES = ['admin'];

// Sync the user's UI language to the backend so emails (order confirmation,
// abandoned-cart reminder, retention reminder) match what they read in the UI.
const LanguageSync = () => {
  const { user, token } = useAuth();
  const { language } = useLanguage();

  useEffect(() => {
    if (!user || !token) return;
    fetch(`${API_BASE}/api/auth/preferred-language`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ language }),
    }).catch(() => { /* non-blocking */ });
  }, [language, user, token]);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/gallery" element={
        <ProtectedRoute>
          <Layout><PhotoGallery /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/gallery/:classId" element={
        <ProtectedRoute>
          <Layout><PhotoGallery /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/cart" element={
        <ProtectedRoute>
          <Layout><Cart /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/checkout/success" element={
        <ProtectedRoute>
          <Layout><CheckoutSuccess /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/orders" element={
        <ProtectedRoute>
          <Layout><Orders /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/link-student" element={
        <ProtectedRoute>
          <Layout><LinkStudent /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/classes" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminClasses /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/students" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminStudents /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/photos" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminPhotos /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminUsers /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/print-formats" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminPrintFormats /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/bundles" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminBundles /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/site-settings" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminSiteSettings /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminAnalytics /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/admin/abandoned-carts" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <Layout><AdminAbandonedCarts /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function AppShell() {
  const [gaId, setGaId] = useState(null);

  useEffect(() => {
    fetch(API_BASE + '/api/site-settings')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.google_analytics_id) setGaId(d.google_analytics_id); })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" aria-hidden="true"></div>
      <AppRouter />
      <LanguageSync />
      <GoogleAnalytics gaId={gaId} />
      <CookieConsent />
      <Toaster 
        position="bottom-right" 
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            border: '1px solid hsl(var(--border))',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <CartProvider>
              <AppShell />
            </CartProvider>
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
